import { HttpBadRequest } from '@httpx/exception';
import type { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from 'next';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import { MainLayout } from '@/components/layout/MainLayout';
import FundSection from '@/components/sections/FundSection';
import { VCService } from '../../services/VCService';
import type { VCFund, FilterOptions } from '../../models/vcFund';
import { toSlug } from '@/lib/util';
import path from 'path';
import fs from 'fs/promises';

// Define Props type
interface Props {
    fund: VCFund;
    options: FilterOptions;
}

// Cache interface
interface CacheData {
    timestamp: number;
    funds: VCFund[];
}

export default function FundPage({ fund, options }: InferGetStaticPropsType<typeof getStaticProps>) {
    const router = useRouter();

    if (router.isFallback) {
        return <div>Loading...</div>;
    }

    return (
        <>
            <NextSeo
                title={`${fund.Title} | Investment Office Türkiye`}
                description={fund.Summary}
                twitter={{
                    cardType: 'summary_large_image',
                }}
                openGraph={{
                    title: fund.Title,
                    description: fund.Summary,
                }}
            />
            <MainLayout>
                <FundSection fund={fund} options={options} />
            </MainLayout>
        </>
    );
}

// Cache helper functions
const CACHE_FILE = path.join(process.cwd(), '.next/cache/funds-data.json');
// const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

async function saveFundsToCache(funds: VCFund[]): Promise<void> {
    try {
        const cacheData: CacheData = {
            timestamp: Date.now(),
            funds
        };
        await fs.mkdir(path.dirname(CACHE_FILE), { recursive: true });
        await fs.writeFile(CACHE_FILE, JSON.stringify(cacheData));
    } catch (error) {
        console.error('Error saving funds to cache:', error);
    }
}

// async function getFundsFromCache(): Promise<VCFund[] | null> {
//     try {
//         const data = await fs.readFile(CACHE_FILE, 'utf8');
//         const cacheData: CacheData = JSON.parse(data);
        
//         // Check if cache is expired (5 minutes)
//         const isExpired = Date.now() - cacheData.timestamp > CACHE_DURATION;
        
//         if (isExpired) {
//             return null;
//         }
        
//         return cacheData.funds;
//     } catch (error) {
//         return null;
//     }
// }

export const getStaticPaths: GetStaticPaths<{ slug: string }> = async () => {
    try {
        // Fetch all funds
        const funds = await VCService.getVCList();
        
        // Save to cache for getStaticProps
        await saveFundsToCache(funds);

        // Only pre-render the most important funds at build time
        // You can adjust the number based on your needs (e.g., most viewed funds)
        const topFunds = funds.slice(0, 20); 
        
        const paths = topFunds.map((fund) => ({
            params: {
                slug: toSlug(fund.Title),
            },
        }));

        return {
            paths,
            // Change to true to enable on-demand generation of new pages
            fallback: 'blocking',
        };
    } catch (error) {
        console.error('Error in getStaticPaths:', error);
        return {
            paths: [],
            fallback: true,
        };
    }
};

export const getStaticProps: GetStaticProps<Props> = async (context) => {
    try {
        const { params } = context;
        const slug = params?.slug as string;

        if (!slug) {
            throw new HttpBadRequest('slug is missing');
        }

        // API çağrısı öncesi log
        console.log(`[${new Date().toISOString()}] Fetching data for slug: ${slug}`);
        
        const funds = await VCService.getVCList();
        
        // Gelen veriyi detaylı loglayalım
        // console.log(`[${new Date().toISOString()}] API Response:`, {
        //     totalFunds: funds.length,
        //     fundTitles: funds.map(f => f.Title),
        //     requestedSlug: slug
        // });

        // İstenen fonu bulalım
        const fund = funds.find((f) => toSlug(f.Title) === slug);
        
        // Bulunan veya bulunamayan fon durumunu loglayalım
        if (fund) {
            console.log(`[${new Date().toISOString()}] Found fund:`, {
                title: fund.Title,
                slug: toSlug(fund.Title),
                matchedSlug: slug
            });
        } else {
            console.log(`[${new Date().toISOString()}] Fund not found for slug:`, {
                requestedSlug: slug,
                availableSlugs: funds.map(f => toSlug(f.Title))
            });
        }

        const options = await VCService.getFilterOptions();

        if (!fund) {
            return {
                notFound: true,
            };
        }

        return {
            props: {
                fund,
                options
            },
            revalidate: 300
        };
    } catch (error) {
        // Hata durumunda detaylı log
        console.error(`[${new Date().toISOString()}] Error in getStaticProps:`, {
            error: error instanceof Error ? {
                message: error.message,
                stack: error.stack
            } : error,
            slug: context.params?.slug
        });
        
        return {
            notFound: true,
        };
    }
};